/**
 * Validation utilities for financial input components.
 * These functions validate card numbers, routing numbers, account numbers,
 * and postal codes for different regions and card types.
 */

// Regex patterns for validation
export const regex = {
  cardAmex: /^3[47][0-9]{13}$/,
  expirationDate: /^(0[1-9]|1[0-2])([0-9]{2})$/,

  // Postal code patterns
  auPostcode: /^\d{4}$/i,
  caPostcode: /^[ABCEGHJKLMNPRSTVXY][0-9][ABCEGHJKLMNPRSTVWXYZ][\s]?[0-9][ABCEGHJKLMNPRSTVWXYZ][0-9]$/i,
  gbPostcode:
    /^([Gg][Ii][Rr]\s?0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))\s?[0-9][A-Za-z]{2})$/i,
  usPostcode: /^\d{5}$/i,
}

// Card validation functions
export const isAmex = (cardNumber: string): boolean => {
  return regex.cardAmex.test(cardNumber)
}

export const isFirstDigitsAmex = (cardNumber: string): boolean => {
  return cardNumber ? cardNumber.slice(0, 2) === '34' || cardNumber.slice(0, 2) === '37' : false
}

export const isValidCardNumber = (cardNumber: string): boolean => {
  // TODO: add luhn algorithm validation
  const number = cardNumber.replace(/\s/g, '')
  const cardLength = isAmex(number) ? 15 : 16
  const validationRegEx = new RegExp(`^\\d{${cardLength}}$`)
  return validationRegEx.test(number)
}

// Bank routing number validation
export const isValidRoutingNumberUsa = (routingNumber: string): boolean => {
  return /^\d{9}$/.test(routingNumber)
}

export const isValidInstitutiontNumberCa = (institutionNumber: string) => {
  return /^\d{3}$/.test(institutionNumber)
}

export const isValidRoutingNumberGb = (routingNumber: string): boolean => {
  return /^\d{6}$/.test(routingNumber)
}

export const isValidBsbAu = (bsbNumber: string): boolean => {
  return /^\d{6}$/.test(bsbNumber)
}

// Bank account number validation
export const isValidBankAccountNumberUsa = (number: string): boolean => {
  return /^\d{4,17}$/.test(number)
}

export const isValidBankAccountNumberCad = (number: string): boolean => {
  return /^\d{4,12}$/.test(number)
}

export const isValidBankAccountNumberAus = (number: string): boolean => {
  return /^\d{6,10}$/.test(number)
}

export const isValidBankAccountNumberGb = (number: string): boolean => {
  return /^\d{7,9}$/.test(number)
}

const parseExpirationDate = (date: string) => {
  const matches = date.replace(/\//g, '').match(regex.expirationDate)
  if (!matches) {
    return null
  }

  const year = parseInt(matches[2], 10)
  const month = parseInt(matches[1], 10)

  return { month, year }
}

const isDatePast = ({ month, year }: { month: number; year: number }) => {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear() % 100
  const currentMonth = currentDate.getMonth() + 1

  return year < currentYear || (year === currentYear && month < currentMonth)
}

export type ExpirationDateValidationError = 'invalidDate' | 'pastDate'
export const isValidExpirationDate = (date: string): { isValid: boolean; reason?: ExpirationDateValidationError } => {
  const expirationMonthYear = parseExpirationDate(date)
  if (!expirationMonthYear) {
    return { isValid: false, reason: 'invalidDate' }
  }

  const isPast = isDatePast(expirationMonthYear)
  return { isValid: !isPast, reason: isPast ? 'pastDate' : undefined }
}
